import React, { memo, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Box, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const dialogStyles = {
  paper: {
    borderRadius: 2,
    minHeight: 200,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(4px)',
  },
  content: {
    p: 0,
  },
  centerBox: {
    textAlign: 'center',
    py: 4,
  },
  iconLarge: {
    fontSize: 60,
    mb: 3,
  },
  title: {
    mb: 2,
    fontWeight: 600,
  },
  subtitle: {
    mb: 3,
  },
  buttonContainer: {
    display: 'flex',
    gap: 2,
    justifyContent: 'center',
  },
  button: {
    minWidth: 120,
  },
};

const LoadingContent = memo(() => (
  <Box sx={dialogStyles.centerBox}>
    <CircularProgress size={60} sx={{ ...dialogStyles.iconLarge, color: 'primary.main' }} />
    <Typography variant='h6' sx={dialogStyles.title}>
      Enviando formulario...
    </Typography>
    <Typography variant='body2' color='text.secondary'>
      Por favor espera mientras procesamos tu solicitud
    </Typography>
  </Box>
));

LoadingContent.displayName = 'LoadingContent';

const SuccessContent = memo(({ onClose }) => {
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  return (
    <Box sx={dialogStyles.centerBox}>
      <CheckCircleIcon sx={{ ...dialogStyles.iconLarge, color: 'success.main' }} />
      <Typography variant='h6' sx={{ ...dialogStyles.title, color: 'success.main' }}>
        ¡Formulario enviado exitosamente!
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={dialogStyles.subtitle}>
        Tu solicitud ha sido procesada correctamente
      </Typography>
      <Button variant='contained' color='success' onClick={handleClose} sx={dialogStyles.button}>
        Continuar
      </Button>
    </Box>
  );
});

SuccessContent.displayName = 'SuccessContent';

const ErrorContent = memo(({ onClose, onRetry }) => {
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleRetry = useCallback(() => {
    onRetry?.();
  }, [onRetry]);

  return (
    <Box sx={dialogStyles.centerBox}>
      <ErrorIcon sx={{ ...dialogStyles.iconLarge, color: 'error.main' }} />
      <Typography variant='h6' sx={{ ...dialogStyles.title, color: 'error.main' }}>
        Error al enviar formulario
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={dialogStyles.subtitle}>
        Ocurrió un problema al procesar tu solicitud
      </Typography>
      <Box sx={dialogStyles.buttonContainer}>
        <Button variant='contained' color='primary' onClick={handleRetry} sx={dialogStyles.button}>
          Reintentar
        </Button>
        <Button variant='outlined' onClick={handleClose} sx={dialogStyles.button}>
          Cancelar
        </Button>
      </Box>
    </Box>
  );
});

ErrorContent.displayName = 'ErrorContent';

const FormSubmissionModal = memo(({ open, status, onClose, onRetry }) => {
  const modalContent = useMemo(() => {
    switch (status) {
      case 'sending':
        return <LoadingContent />;
      case 'success':
        return <SuccessContent onClose={onClose} />;
      case 'error':
        return <ErrorContent onClose={onClose} onRetry={onRetry} />;
      default:
        return null;
    }
  }, [status, onClose, onRetry]);

  const handleClose = useCallback(() => {
    if (status !== 'sending') {
      onClose?.();
    }
  }, [status, onClose]);

  const dialogProps = useMemo(
    () => ({
      open,
      onClose: handleClose,
      maxWidth: 'sm',
      fullWidth: true,
      PaperProps: {
        sx: dialogStyles.paper,
      },
      BackdropProps: {
        sx: dialogStyles.backdrop,
      },
    }),
    [open, handleClose]
  );

  return (
    <Dialog {...dialogProps}>
      <DialogContent sx={dialogStyles.content}>{modalContent}</DialogContent>
    </Dialog>
  );
});

FormSubmissionModal.displayName = 'FormSubmissionModal';

export default FormSubmissionModal;
